import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "./index";
import { AssociationResponse } from "../types/association.types";
import { resolveSelectedAssociationId } from "../utils/associationSelection";

// Association portal: the administered associations and which one the pages
// are showing. Mirrors `advisorContext`: a normal load, a background refetch
// that never blanks the page, a retry for a failed first load, and a reset
// when the persona ends. An empty list is a LOADED state (the account
// administers nothing), never an error.
//
// `GET /association/mine` is read from several places at once (the loader,
// the 30 s tick, every card's post-save re-read, the roster refresh), so two
// requests can overlap and land out of order. Every request carries a
// sequence number, and only the answer to the LATEST started request is
// applied: an older answer, success or failure, is dropped.

export type AssociationContextStatus = "idle" | "loading" | "loaded" | "error";

export interface AssociationContextState {
  associations: AssociationResponse[];
  selectedId: number | null;
  status: AssociationContextStatus;
  /** True after a fetch that FAILED; the last loaded list is kept. */
  loadError: boolean;
  /**
   * The sequence number of the latest started `/mine` request; 0 when none
   * started since the last reset, so no in-flight answer matches it.
   */
  requestSeq: number;
}

const initialState: AssociationContextState = {
  associations: [],
  selectedId: null,
  status: "idle",
  loadError: false,
  requestSeq: 0,
};

const associationContextSlice = createSlice({
  name: "associationContext",
  initialState,
  reducers: {
    /**
     * Takes the request's sequence number, which supersedes every request
     * still in flight. A refetch over a loaded list stays "loaded": no
     * spinner over live content.
     */
    fetchStart: (state, action: PayloadAction<number>) => {
      state.requestSeq = action.payload;
      state.status = state.status === "loaded" ? "loaded" : "loading";
      state.loadError = false;
    },
    /** Ignored unless it answers the latest started request. */
    fetchSucceeded: (
      state,
      action: PayloadAction<{
        associations: AssociationResponse[];
        rememberedId: number | null;
        seq: number;
      }>,
    ) => {
      const { associations, rememberedId, seq } = action.payload;
      if (seq !== state.requestSeq) {
        return;
      }
      state.associations = associations;
      state.selectedId = resolveSelectedAssociationId(
        associations,
        state.selectedId,
        rememberedId,
      );
      state.status = "loaded";
      state.loadError = false;
    },
    /**
     * Network, timeout, 4xx, 5xx. A failed request is not evidence that the
     * associations are gone, so the last known list is preserved; `error` is
     * only reachable when no load ever succeeded. An EMPTY loaded list stays
     * loaded too: it is an answer ("administers nothing"), not a failure.
     * Ignored unless it answers the latest started request: a superseded
     * failure says nothing about the newer answer.
     */
    fetchFailed: (state, action: PayloadAction<number>) => {
      if (action.payload !== state.requestSeq) {
        return;
      }
      state.status = state.status === "loaded" ? "loaded" : "error";
      state.loadError = true;
    },
    /** Ignores ids that are not in the list: the pages never show a phantom. */
    selected: (state, action: PayloadAction<number>) => {
      if (state.associations.some((a) => a.id === action.payload)) {
        state.selectedId = action.payload;
      }
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    // The context belongs to one authenticated persona and must not outlive
    // it. The reset also clears `requestSeq`, so no answer still in flight
    // can land in the next persona's context.
    builder.addCase("auth/logout", () => initialState);
    builder.addCase("auth/personaExit", () => initialState);
  },
});

export const associationContextActions = associationContextSlice.actions;

export const selectAssociationContextState = (state: RootState) =>
  state.associationContext;

export const selectAssociations = (state: RootState) =>
  state.associationContext.associations;

export const selectSelectedAssociationId = (state: RootState) =>
  state.associationContext.selectedId;

/** The ONE selector every association page reads its subject from. */
export const selectSelectedAssociation = createSelector(
  [selectAssociations, selectSelectedAssociationId],
  (associations, selectedId): AssociationResponse | null =>
    selectedId == null
      ? null
      : associations.find((a) => a.id === selectedId) ?? null,
);

export default associationContextSlice;
